import React, { useState, useEffect, useCallback } from 'react'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  AlertTriangle,
  Calendar,
  RefreshCw,
  Loader2,
  Tag,
  ShoppingBag,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  LayoutGrid,
} from 'lucide-react'
import { useCurrency } from '@/hooks/useCurrency'
import { ApiService } from '@/services/api'
import { formatDateForInput, createDateRange, parseDate } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'
import { MobileFloatingFilter } from '@/components/ui/MobileFloatingFilter'
import type { DateRange } from '@/types'

type CategoryItem = {
  name: string
  ordersCount: number
  itemsSold: number
  revenue: number
  percentage: number
  topItems: Array<{ name: string; quantity: number; revenue: number; baseRevenue: number; chargesAmount: number }>
}

type CategoryData = {
  categories: CategoryItem[]
  summary: {
    totalCategories: number
    totalOrders: number
    totalItems: number
    totalRevenue: number
  }
}

const BAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-orange-500',
  'bg-pink-500', 'bg-teal-500', 'bg-yellow-500', 'bg-red-500',
]

export default function CategoryAnalysisScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const { currentRestaurant } = useRestaurant()
  const [data, setData] = useState<CategoryData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())

  const loadData = useCallback(async () => {
    if (!currentRestaurant?._id) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getCategoryAnalysis(currentRestaurant._id, dateRange)
      if (response.success) {
        setData(response.data)
      } else {
        setError(response.message || 'Failed to load category analysis')
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to load category analysis'
        : 'Failed to load category analysis'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }, [currentRestaurant?._id, dateRange])

  useEffect(() => {
    if (currentRestaurant?._id) loadData()
  }, [currentRestaurant?._id, loadData])

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    const newDate = parseDate(value)
    setDateRange(prev =>
      field === 'from' ? createDateRange(newDate, prev.to) : createDateRange(prev.from, newDate)
    )
  }

  const toggleCategory = (name: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      next.has(name) ? next.delete(name) : next.add(name)
      return next
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading category analysis...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button variant="outline" size="sm" className="ml-2 py-[10px]" onClick={() => loadData()}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const { categories = [], summary = { totalCategories: 0, totalOrders: 0, totalItems: 0, totalRevenue: 0 } } = data || {}

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">Category Analysis</h1>
          <p className="text-muted-foreground text-sm sm:text-base truncate">
            Orders and revenue broken down by food category
          </p>
        </div>
        <Button onClick={() => loadData()} variant="outline" size="icon" disabled={isLoading} className="py-[10px] sm:w-auto sm:h-auto sm:px-4 flex-shrink-0">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Date Range — Desktop */}
      <Card className="hidden md:block">
        <CardHeader>
          <CardTitle className="text-base flex items-center space-x-2">
            <Calendar className="h-4 w-4" />
            <span>Analysis Period</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="from">From</Label>
              <Input id="from" type="date" value={formatDateForInput(dateRange.from)} onChange={(e) => handleDateChange('from', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="to">To</Label>
              <Input id="to" type="date" value={formatDateForInput(dateRange.to)} onChange={(e) => handleDateChange('to', e.target.value)} />
            </div>
            <div className="col-span-2 flex items-end">
              <Button onClick={() => loadData()} className="w-full lg:w-auto">Apply Filters</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Categories</CardTitle>
            <LayoutGrid className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{summary.totalCategories}</div>
            <p className="text-xs text-muted-foreground">Active categories</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <ShoppingBag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{summary.totalOrders}</div>
            <p className="text-xs text-muted-foreground">Bills in period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Items Sold</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{summary.totalItems}</div>
            <p className="text-xs text-muted-foreground">Total quantity</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(summary.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground">From all categories</p>
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <LayoutGrid className="h-5 w-5" />
            <span>Category Breakdown</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {categories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No category data found for this period.
            </div>
          ) : (
            <div className="space-y-3">
              {categories.map((cat, idx) => {
                const color = BAR_COLORS[idx % BAR_COLORS.length]
                const isExpanded = expandedCategories.has(cat.name)
                return (
                  <div key={cat.name} className="border rounded-lg overflow-hidden">
                    {/* Category row */}
                    <button
                      className="w-full p-4 text-left hover:bg-muted/40 transition-colors"
                      onClick={() => toggleCategory(cat.name)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge variant="outline" className="shrink-0 text-xs">
                            #{idx + 1}
                          </Badge>
                          <span className="font-semibold truncate">{cat.name}</span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 ml-2">
                          <span className="text-sm font-medium text-muted-foreground hidden sm:inline">
                            {cat.percentage.toFixed(1)}%
                          </span>
                          {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="w-full h-2 bg-muted rounded-full mb-3">
                        <div
                          className={`h-2 rounded-full ${color}`}
                          style={{ width: `${Math.min(cat.percentage, 100)}%` }}
                        />
                      </div>

                      {/* Stats row */}
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <div className="text-muted-foreground text-xs">Orders</div>
                          <div className="font-semibold">{cat.ordersCount}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground text-xs">Items Sold</div>
                          <div className="font-semibold">{cat.itemsSold}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground text-xs">Revenue</div>
                          <div className="font-semibold">{formatCurrency(cat.revenue)}</div>
                        </div>
                      </div>
                    </button>

                    {/* Expandable top items */}
                    {isExpanded && cat.topItems.length > 0 && (
                      <div className="border-t bg-muted/20 px-4 py-3">
                        <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                          Top Items
                        </p>
                        <div className="space-y-2">
                          {cat.topItems.map((item, i) => (
                            <div key={item.name} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-muted-foreground text-xs w-4 shrink-0">{i + 1}.</span>
                                <span className="truncate">{item.name}</span>
                              </div>
                              <div className="flex flex-col items-end shrink-0 ml-2 text-xs">
                                <div className="flex gap-4 text-muted-foreground">
                                  <span>×{item.quantity}</span>
                                  <span className="font-medium text-foreground">{formatCurrency(item.revenue)}</span>
                                </div>
                                {item.chargesAmount > 0.5 && (
                                  <span className="text-muted-foreground">
                                    {formatCurrency(item.baseRevenue)} item + {formatCurrency(item.chargesAmount)} tax/charges
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile Floating Filter */}
      <MobileFloatingFilter
        dateRange={dateRange}
        onDateChange={handleDateChange}
        onApply={() => loadData()}
        title="Category Analysis Period"
        description="Select the date range for category analysis"
      />
    </div>
  )
}
